import {
  completionTimeBuffer,
  getDateTime,
} from '@pinnacleautomotive/clarity-components';
import { createSlice } from '@reduxjs/toolkit';
import {
  setSelectedVendor,
  getServicesByDepartment,
  addPriceToSelectedPrices,
  removePriceToSelectedPrices,
  getVehicleByVin,
  saveNewOrder,
  clearNewOrder,
  editVehicleDetails,
  addVehicleDetails,
  openEditVehicleModal,
  closeEditVehicleModal,
  setObservations,
  addSpecialRequest,
  removeSpecialRequest,
  changeCompletionType,
  setPromiseDate,
  updateCreatedDate,
  discardOrder,
  clearError,
  setAlternativeOwner,
  alternativeOwner,
  setAllDealers,
  setStockNumber,
} from '../../actions/orders/newOrderActions';

export const initialState = {
  prices: [],
  specialRequests: [],
  alternativeOwner: { label: '', personId: null },
  owners: [],
  alternativeOwnerLoading: false,
  selectedDepartment: '',
  selectedVendor: null,
  selectedPrices: [],
  categories: [],
  selectedCategories: [],
  selectedVehicle: null,
  vehicleNotFound: false,
  isLoading: false,
  isLoadingServices: false,
  isLoadingVehicle: false,
  workOrderSaved: null,
  saveError: false,
  editVehicleModalOpen: false,
  po: null,
  ro: null,
  stockNumber: null,
  vehicleId: null,
  observation: '',
  discarded: false,
  completionType: 'estimated',
  servicesTotalComplianceTime: 0,
  createdDate: new Date().getTime(),
  promiseDate: new Date().getTime(),
  editVehicleDataError: false,
  dealers: [],
};

export const isLoadingReducer = isLoadingItem => state => {
  state[isLoadingItem] = true;
  state.vehicleId = null;
  state.selectedVehicle = null;
  state.isLoading = true;
  state.workOrderSaved = null;
  state.saveError = false;
};

export const saveLoadingReducer = state => {
  state.isLoading = true;
  state.workOrderSaved = null;
  state.saveError = false;
};

export const closeEditVehicleModalReducer = state => {
  state.isLoading = false;
  state.isLoadingVehicle = false;
  state.editVehicleModalOpen = false;
  state.editVehicleDataError = false;
};

export const vehicleDetailPending = (state, { meta: { arg } }) => {
  isLoadingReducer('isLoadingVehicle')(state);
  state.stockNumber = arg.data.custody.stockNumber;
  state.po = arg.data.po;
  state.ro = arg.data.ro;
  state.discarded = false;
};

export const vehicleDetailFulfilled = (state, { payload }) => {
  closeEditVehicleModalReducer(state);
  state.selectedVehicle = {
    ...payload,
    custody: { stockNumber: state.stockNumber },
    po: state.po,
    ro: state.ro,
  };
  state.vehicleNotFound = false;
  state.editVehicleDataError = false;
};

export const vehicleDetailRejected = state => {
  state.isLoading = false;
  state.isLoadingVehicle = false;
  state.editVehicleDataError = true;
};

const newOrderReducer = createSlice({
  name: 'newOrder',
  initialState,
  extraReducers: {
    [saveNewOrder.pending]: saveLoadingReducer,
    [getVehicleByVin.pending]: isLoadingReducer('isLoadingVehicle'),
    [editVehicleDetails.pending]: vehicleDetailPending,
    [addVehicleDetails.pending]: vehicleDetailPending,
    [closeEditVehicleModal.type]: closeEditVehicleModalReducer,
    [editVehicleDetails.fulfilled]: vehicleDetailFulfilled,
    [addVehicleDetails.fulfilled]: vehicleDetailFulfilled,
    [addVehicleDetails.rejected]: vehicleDetailRejected,
    [editVehicleDetails.rejected]: vehicleDetailRejected,
    [setObservations.type]: (state, { payload }) => {
      state.observation = payload;
    },
    [setAlternativeOwner.type]: (state, { payload }) => {
      state.alternativeOwner = payload;
    },
    [addSpecialRequest.type]: (state, { payload }) => {
      state.specialRequests.push(payload);
    },
    [removeSpecialRequest.type]: (state, { payload }) => {
      state.specialRequests.splice(payload, 1);
    },
    [setSelectedVendor.type]: (state, { payload }) => {
      if (state.selectedVendor?.companyId === payload.companyId) {
        state.selectedVendor = null;
        state.alternativeOwner = { label: '', personId: null };
        state.owners = [];
      } else state.selectedVendor = payload;

      state.selectedDepartment = '';
      state.selectedPrices = [];
      state.prices = [];
      state.categories = [];
      state.selectedCategories = [];
    },
    [openEditVehicleModal.type]: state => {
      state.editVehicleModalOpen = true;
    },
    [setPromiseDate.type]: (state, { payload }) => {
      state.promiseDate = payload;
    },
    [getServicesByDepartment.pending]: (state, { meta: { arg } }) => {
      state.isLoading = true;
      state.isLoadingServices = true;
      state.selectedDepartment = arg.department;
      state.prices = [];
      state.selectedPrices = [];
      state.categories = [];
      state.selectedCategories = [];
    },
    [getServicesByDepartment.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.isLoadingServices = false;
      state.prices = payload.prices;
      state.completionTime = 0;
      calculatePromiseDate(state);
    },
    [setAllDealers.pending]: state => {
      state.isLoading = true;
      state.dealers = [];
    },
    [setAllDealers.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.dealers = payload.dealers;
    },
    [setAllDealers.rejected]: state => {
      state.isLoading = false;
      state.dealers = [];
    },
    [addPriceToSelectedPrices.type]: (state, { payload }) => {
      state.selectedPrices.push(payload.servicePriceId);
      state.categories = [...state.categories, payload.service.type];
      state.selectedCategories = [
        ...new Set([...state.categories, payload.service.type]),
      ];
      state.servicesTotalComplianceTime += payload.localComplianceTime;
      calculatePromiseDate(state);
    },
    [removePriceToSelectedPrices.type]: (state, { payload }) => {
      state.selectedPrices = state.selectedPrices.filter(
        price => price !== payload.servicePriceId
      );
      state.categories = state.categories.filter(
        (category, index) =>
          category !== payload.service.type ||
          state.categories.indexOf(category) !== index
      );
      state.selectedCategories = [...new Set(state.categories)];
      state.servicesTotalComplianceTime -= payload.localComplianceTime;
      calculatePromiseDate(state);
    },
    [getVehicleByVin.fulfilled]: (state, { payload }) => {
      state.selectedVehicle = payload;
      state.vehicleNotFound = false;
      state.isLoading = false;
      state.isLoadingVehicle = false;
      state.po = null;
      state.stockNumber = payload?.custody?.stockNumber;
      state.vehicleId = payload?.vehicleId;
      state.ro = null;
    },
    [getVehicleByVin.rejected]: state => {
      state.isLoading = false;
      state.isLoadingVehicle = false;
      state.selectedVehicle = null;
      state.vehicleNotFound = true;
    },
    [setStockNumber.type]: (state, { payload }) => {
      state.stockNumber = payload;
    },
    [saveNewOrder.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.workOrderSaved = payload;
      state.saveError = null;
    },
    [saveNewOrder.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.workOrderSaved = null;
      state.saveError = { token: 'UNEXPECTED_ERROR', ...payload };
    },
    [changeCompletionType.type]: (state, { payload }) => {
      state.completionType = payload;
    },
    [updateCreatedDate.type]: state => {
      state.createdDate = new Date().getTime();
      calculatePromiseDate(state);
    },
    [clearError.type]: state => {
      state.saveError = null;
      state.editVehicleDataError = false;
    },
    [clearNewOrder.type]: () => ({ ...initialState }),
    [discardOrder.type]: () => ({ ...initialState, discarded: true }),
    [alternativeOwner.pending]: state => {
      state.alternativeOwnerLoading = true;
      state.owners = null;
    },
    [alternativeOwner.fulfilled]: (state, { payload }) => {
      state.alternativeOwnerLoading = false;
      state.owners = payload.users;
    },
    [alternativeOwner.rejected]: state => {
      state.alternativeOwnerLoading = false;
    },
    [setAllDealers.type]: (state, { payload }) => {
      state.dealers = payload;
    },
  },
});

function calculatePromiseDate(state) {
  state.promiseDate = getDateTime(new Date(state.createdDate))
    .plus({ minutes: state.servicesTotalComplianceTime })
    .plus({ minutes: completionTimeBuffer * 2 })
    .toJSDate()
    .getTime();
}

export default newOrderReducer;
